import React, { useEffect, useState, useCallback } from "react";
import { Search, Users, MapPin, Loader2, Power } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useCountry } from "@/lib/country-context";
import adminApi from "@/lib/admin-api";

export default function AdminCustomersPage() {
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await adminApi.get("/customers", { params });
      setCustomers(data);
    } catch {
      toast({ title: "Couldn't load customers", variant: "destructive" });
    } finally { setLoading(false); }
  }, [search, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-bold">Customers</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Every customer on Cravve.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by name or phone..." className="pl-9 h-10"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-customers-search" />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        </div>
      ) : customers.length === 0 ? (
        <div className="bg-card border rounded-2xl p-12 text-center text-muted-foreground">
          <Users className="w-12 h-12 mx-auto opacity-20 mb-3" />
          <p className="font-medium">No customers yet</p>
        </div>
      ) : (
        <div className="bg-card border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">Phone</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">Country</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold hidden md:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} onClick={() => setSelectedId(c.id)}
                    className="border-t hover:bg-muted/30 cursor-pointer"
                    data-testid={`admin-customer-row-${c.id}`}>
                    <td className="p-3 font-medium">{c.first_name} {c.last_name}</td>
                    <td className="p-3 text-muted-foreground">{c.phone}</td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">{c.country}</td>
                    <td className="p-3">
                      {c.is_active === false ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Suspended</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Active</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right text-xs text-muted-foreground hidden md:table-cell">
                      {new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedId && (
        <CustomerDetailDialog id={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
      )}
    </div>
  );
}

function CustomerDetailDialog({ id, onClose, onChanged }) {
  const { formatPrice } = useCountry();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    adminApi.get(`/customers/${id}`).then(({ data }) => setData(data));
  }, [id]);

  const toggleActive = async () => {
    setUpdating(true);
    try {
      const newState = data.is_active === false;
      await adminApi.patch(`/customers/${id}`, { is_active: newState });
      setData({ ...data, is_active: newState });
      toast({ title: newState ? "Customer reactivated" : "Customer suspended" });
      onChanged();
    } catch (e) {
      toast({ title: "Update failed", variant: "destructive" });
    } finally { setUpdating(false); }
  };

  if (!data) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{data.first_name} {data.last_name}</DialogTitle>
          <p className="text-sm text-muted-foreground">{data.phone} • {data.country}</p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="bg-muted/30 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
            <Stat label="Total orders" value={data.stats?.total_orders ?? 0} />
            <Stat label="Total spent" value={formatPrice(data.stats?.total_spent ?? 0)} />
            <Stat label="Joined" value={new Date(data.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })} />
          </div>

          {data.latitude && data.longitude && (
            <div className="bg-card border rounded-xl p-3 flex items-center gap-2 text-xs">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">
                Signup location: {data.latitude.toFixed(4)}, {data.longitude.toFixed(4)}
              </span>
            </div>
          )}

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Recent orders</p>
            {data.orders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No orders yet.</p>
            ) : (
              <div className="space-y-2">
                {data.orders.slice(0, 10).map((o) => (
                  <div key={o.id} className="bg-card border rounded-lg p-3 text-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{o.restaurant_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {o.status.replace(/_/g, " ")} • {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </p>
                      </div>
                      <p className="font-bold">{formatPrice(o.total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button variant="outline" disabled={updating} onClick={toggleActive}
            className={`w-full gap-2 ${data.is_active === false ? "" : "border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"}`}
            data-testid="admin-customer-toggle-active">
            <Power className="w-4 h-4" /> {data.is_active === false ? "Reactivate account" : "Suspend account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-serif font-bold text-base mt-0.5">{value}</p>
    </div>
  );
}
